/**
 * Dinamik RBAC — paylaşılan katalog (frontend + backend ortak).
 *
 * - PERMISSION_KEYS: tüm yetki anahtarları (backend PERMISSIONS matrisiyle birebir).
 * - RESOURCE_MAP: admin ekranı taksonomisi (sidebar aynası) — Bölüm → Sayfa → Okuma/Yazma/Gelişmiş.
 * - BUILTIN_ROLES: sistem rollerinin görünen ad/renk meta'sı.
 *
 * NOT: Görünen metinler (section/page/advanced) i18n `roles` namespace'inden çevrilir;
 * buradaki Türkçe stringler yalnızca fallback (defaultValue). Rol adları frontend'de
 * `enums:userRole.<key>` ile çevrilir.
 */

// ─── Yetki anahtarı kataloğu ──────────────────────────────────────────────────
export const PERMISSION_KEYS = [
  'PROPOSAL_VIEW', 'PROPOSAL_WRITE', 'PROPOSAL_STATUS', 'PROPOSAL_DELETE',
  'PROCUREMENT_VIEW', 'PROCUREMENT_WRITE', 'PROCUREMENT_AWARD', 'PROCUREMENT_DELETE',
  'CUSTOMER_VIEW', 'CUSTOMER_WRITE', 'CUSTOMER_DELETE',
  'PROJECT_VIEW', 'PROJECT_WRITE', 'PROJECT_PROGRESS_WRITE', 'PROJECT_BOQ_VIEW', 'PROJECT_DELETE',
  'PROJECT_DOCUMENT_VIEW', 'PROJECT_DOCUMENT_WRITE',
  'PRODUCTION_VIEW', 'PRODUCTION_WRITE',
  'PROJECT_TEMPLATE_VIEW', 'PROJECT_TEMPLATE_WRITE', 'PROJECT_TEMPLATE_DELETE',
  'TIMESHEET_VIEW', 'TIMESHEET_WRITE', 'TIMESHEET_APPROVE', 'TIMESHEET_MONTHLY_CLOSE',
  'LEAVE_VIEW', 'LEAVE_WRITE', 'LEAVE_APPROVE', 'LEAVE_TYPE_MANAGE',
  'FINANCE_OVERVIEW_VIEW',
  'REVENUE_VIEW', 'REVENUE_WRITE', 'REVENUE_APPROVE', 'REVENUE_HAKEDIS_STATUS', 'SCHEDULED_RECEIPT_NOTIFY',
  'EXPENSE_VIEW', 'EXPENSE_WRITE', 'EXPENSE_SETTINGS', 'SCHEDULED_PAYMENT_NOTIFY',
  'SUBCON_VIEW', 'SUBCON_WRITE', 'SUBCON_FINANCE_WRITE', 'SUBCON_APPROVE', 'SUBCON_DELETE',
  'INSTRUMENT_VIEW', 'INSTRUMENT_WRITE',
  'REPORT_VIEW', 'REPORT_PROPOSAL_VIEW',
  'NOTIFICATION_VIEW', 'NOTIFICATION_WRITE', 'NOTIFICATION_BROADCAST',
  'USER_VIEW', 'USER_WRITE', 'USER_ROLE_CHANGE', 'USER_DEACTIVATE', 'USER_PURGE',
  'USER_DOCUMENT_VIEW', 'USER_DOCUMENT_WRITE',
  'ROLE_MANAGE',
  'SETTINGS_VIEW', 'SETTINGS_WRITE', 'BILLING_MANAGE', 'OFFICE_WRITE',
  'PAYMENT_CATEGORY_VIEW', 'PAYMENT_CATEGORY_WRITE', 'AUDIT_VIEW',
  'DATACENTER_VIEW', 'DATACENTER_MANAGE', 'DATACENTER_BACKUP_RESTORE',
  'AI_USE', 'AI_ADMIN',
] as const

export type PermissionKey = (typeof PERMISSION_KEYS)[number]

export const isPermissionKey = (v: string): v is PermissionKey =>
  (PERMISSION_KEYS as readonly string[]).includes(v)

// ─── Sistem rolleri (görünen meta) ────────────────────────────────────────────
// name = fallback (frontend `enums:userRole.<key>` ile çevirir).
export interface BuiltinRole {
  key: string
  name: string
  color: string
}

export const BUILTIN_ROLES: readonly BuiltinRole[] = [
  { key: 'TENANT_ADMIN', name: 'Yönetici', color: '#6366f1' },
  { key: 'PM', name: 'Proje Yöneticisi', color: '#3b82f6' },
  { key: 'TL', name: 'Takım Lideri', color: '#0ea5e9' },
  { key: 'EMP', name: 'Çalışan', color: '#64748b' },
  { key: 'MGMT', name: 'Üst Yönetim', color: '#8b5cf6' },
  { key: 'CR', name: 'Müşteri İlişkileri', color: '#14b8a6' },
  { key: 'ACCOUNTANT', name: 'Muhasebe', color: '#f59e0b' },
]

// ─── Taksonomi (admin ekranı — sidebar aynası) ────────────────────────────────
export interface AdvancedPerm {
  key: PermissionKey
  /** i18n fallback; çeviri: roles:perm.<key> */
  label: string
}

export interface ResourcePage {
  /** i18n anahtarı → roles:page.<key> */
  key: string
  /** Görünen sayfa adı (i18n fallback) */
  page: string
  /** Sidebar rotası (nav öğesiyse) — nav görünürlüğü `read`/`write` anahtarına bağlanır */
  route?: string
  /** Okuma sütunu anahtarı */
  read?: PermissionKey
  /** Yazma sütunu anahtarı (yoksa salt-okunur) */
  write?: PermissionKey
  /** ▸Gelişmiş içindeki hassas aksiyonlar */
  advanced?: AdvancedPerm[]
  /** Alt kaynak (nav öğesi değil, üst sayfanın altında) */
  sub?: boolean
}

export interface ResourceSection {
  /** i18n anahtarı → roles:section.<key> */
  key: string
  /** Görünen bölüm adı (i18n fallback) */
  section: string
  pages: ResourcePage[]
}

export const RESOURCE_MAP: readonly ResourceSection[] = [
  {
    key: 'business', section: 'İş Geliştirme',
    pages: [
      {
        key: 'proposals', page: 'Teklifler', route: '/proposals',
        read: 'PROPOSAL_VIEW', write: 'PROPOSAL_WRITE',
        advanced: [
          { key: 'PROPOSAL_STATUS', label: 'Durum / Onay / Projeye çevir' },
          { key: 'PROPOSAL_DELETE', label: 'Sil' },
        ],
      },
      {
        // Teklifler sayfası içindeki "Alınan Teklifler" sekmesi + detay (teklif toplama & kıyaslama)
        key: 'procurement', page: 'Alınan Teklifler', route: '/proposals/received', sub: true,
        read: 'PROCUREMENT_VIEW', write: 'PROCUREMENT_WRITE',
        advanced: [
          { key: 'PROCUREMENT_AWARD', label: 'Kazananı seç & dönüştür' },
          { key: 'PROCUREMENT_DELETE', label: 'Sil' },
        ],
      },
      {
        key: 'customers', page: 'Müşteriler', route: '/customers',
        read: 'CUSTOMER_VIEW', write: 'CUSTOMER_WRITE',
        advanced: [{ key: 'CUSTOMER_DELETE', label: 'Sil' }],
      },
    ],
  },
  {
    key: 'projects', section: 'Proje Yönetimi',
    pages: [
      {
        key: 'projects', page: 'Projeler', route: '/projects',
        read: 'PROJECT_VIEW', write: 'PROJECT_WRITE',
        advanced: [
          { key: 'PROJECT_PROGRESS_WRITE', label: 'İlerleme yönetimi (grup/kalem)' },
          { key: 'PROJECT_BOQ_VIEW', label: 'BoQ görüntüleme' },
          { key: 'PROJECT_DOCUMENT_VIEW', label: 'Belge görüntüleme' },
          { key: 'PROJECT_DOCUMENT_WRITE', label: 'Belge yönetimi (yükle/düzenle/sil)' },
          { key: 'PROJECT_DELETE', label: 'Arşivle (sil)' },
        ],
      },
      { key: 'production', page: 'İmalat Kayıtları', sub: true, read: 'PRODUCTION_VIEW', write: 'PRODUCTION_WRITE' },
      {
        key: 'templates', page: 'Proje Şablonları', sub: true,
        read: 'PROJECT_TEMPLATE_VIEW', write: 'PROJECT_TEMPLATE_WRITE',
        advanced: [{ key: 'PROJECT_TEMPLATE_DELETE', label: 'Sil' }],
      },
      {
        key: 'timesheets', page: 'Çalışma Kaydı', route: '/timesheets',
        read: 'TIMESHEET_VIEW', write: 'TIMESHEET_WRITE',
        advanced: [
          { key: 'TIMESHEET_APPROVE', label: 'Onayla / Reddet' },
          { key: 'TIMESHEET_MONTHLY_CLOSE', label: 'Aylık kapanış' },
        ],
      },
      {
        key: 'leave', page: 'İzin', route: '/leave',
        read: 'LEAVE_VIEW', write: 'LEAVE_WRITE',
        advanced: [
          { key: 'LEAVE_APPROVE', label: 'Onayla / Reddet' },
          { key: 'LEAVE_TYPE_MANAGE', label: 'İzin tipi & bakiye yönetimi' },
        ],
      },
    ],
  },
  {
    key: 'finance', section: 'Finans',
    pages: [
      { key: 'financeOverview', page: 'Genel Bakış', route: '/finance/overview', read: 'FINANCE_OVERVIEW_VIEW' },
      {
        key: 'revenue', page: 'Gelirler', route: '/revenue',
        read: 'REVENUE_VIEW', write: 'REVENUE_WRITE',
        advanced: [
          { key: 'REVENUE_APPROVE', label: 'Hakediş onay / ret' },
          { key: 'REVENUE_HAKEDIS_STATUS', label: 'Hakediş durum geçişi' },
          { key: 'SCHEDULED_RECEIPT_NOTIFY', label: 'Planlanan tahsilat hatırlatmaları' },
        ],
      },
      {
        key: 'expenses', page: 'Giderler', route: '/finance',
        read: 'EXPENSE_VIEW', write: 'EXPENSE_WRITE',
        advanced: [
          { key: 'EXPENSE_SETTINGS', label: 'Finans ayarları & maaş profili' },
          { key: 'SCHEDULED_PAYMENT_NOTIFY', label: 'Planlanan ödeme hatırlatmaları' },
        ],
      },
      {
        key: 'subConsultants', page: 'Alt Yükleniciler', route: '/sub-consultants',
        read: 'SUBCON_VIEW', write: 'SUBCON_WRITE',
        advanced: [
          { key: 'SUBCON_FINANCE_WRITE', label: 'Finans yazma (vendor/sertifika/fatura-ödendi)' },
          { key: 'SUBCON_APPROVE', label: 'Sözleşme durumu & değişiklik emri onay' },
          { key: 'SUBCON_DELETE', label: 'Sil (sözleşme/vendor/BoQ)' },
        ],
      },
      { key: 'instruments', page: 'Çek / Senet', route: '/instruments', read: 'INSTRUMENT_VIEW', write: 'INSTRUMENT_WRITE' },
    ],
  },
  {
    key: 'other', section: 'Diğer',
    pages: [
      {
        key: 'reports', page: 'Raporlar', route: '/reports', read: 'REPORT_VIEW',
        advanced: [{ key: 'REPORT_PROPOSAL_VIEW', label: 'Teklif raporu' }],
      },
      {
        key: 'notifications', page: 'Bildirimler', route: '/notifications',
        read: 'NOTIFICATION_VIEW', write: 'NOTIFICATION_WRITE',
        advanced: [{ key: 'NOTIFICATION_BROADCAST', label: 'Toplu bildirim (broadcast)' }],
      },
      {
        // Tam ekran çalışma alanı (/ai) + her sayfada açılan AI paneli + Ayarlar > Yapay Zekâ sekmesi (AI_ADMIN)
        key: 'ai', page: 'Yapay Zekâ Asistanı', route: '/ai', read: 'AI_USE',
        advanced: [{ key: 'AI_ADMIN', label: 'Yapay Zekâ yönetimi (ayarlar, limitler, tüm kullanıcıların kullanımı)' }],
      },
    ],
  },
  {
    key: 'admin', section: 'Yönetim',
    pages: [
      {
        key: 'users', page: 'Kullanıcılar', route: '/users',
        read: 'USER_VIEW', write: 'USER_WRITE',
        advanced: [
          { key: 'USER_DEACTIVATE', label: 'Pasife al' },
          { key: 'USER_PURGE', label: 'Kalıcı sil (kaydı olmayan kullanıcı)' },
          { key: 'USER_DOCUMENT_VIEW', label: 'Belge görüntüleme (kimlik/evrak)' },
          { key: 'USER_DOCUMENT_WRITE', label: 'Belge yönetimi (yükle/düzenle/sil)' },
        ],
      },
      { key: 'roles', page: 'Roller & Yetkiler', write: 'ROLE_MANAGE' },
      {
        key: 'settings', page: 'Ayarlar', route: '/settings',
        read: 'SETTINGS_VIEW', write: 'SETTINGS_WRITE',
        advanced: [
          { key: 'BILLING_MANAGE', label: 'Abonelik & Ödeme' },
          { key: 'OFFICE_WRITE', label: 'Ofisler' },
          { key: 'PAYMENT_CATEGORY_VIEW', label: 'Ödeme kategorileri (okuma)' },
          { key: 'PAYMENT_CATEGORY_WRITE', label: 'Ödeme kategorileri (yazma)' },
          { key: 'AUDIT_VIEW', label: 'Denetim günlüğü' },
          { key: 'DATACENTER_VIEW', label: 'Veri Merkezi (görüntüleme)' },
          { key: 'DATACENTER_MANAGE', label: 'Veri Merkezi (yönetim: ağ, güvenlik, cihazlar, yedek)' },
          { key: 'DATACENTER_BACKUP_RESTORE', label: 'Yedekten geri yükleme' },
        ],
      },
    ],
  },
]
